/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

/** * NOTE  ***/
/* Make sure to reset this flag{_isPdfDirtyByJS} whenever starting a new edit operation which dirties the document.This flag is modified explicitly by javascript API and its value
   has to be reset with every api call which dirties document. So make sure to add below line of code before dispatching message object while adding new API which will edit PDF.
   this._isPdfDirtyByJS = STATUS.UNDEFINED;*/
import * as constants from "constants/constants";
import { providers, locale2 } from "dc-core";
import {
  trackPageLoadFailed,
  trackUnembeddedFonts,
  trackHasUnembeddedFonts,
  trackJP2KAnalytics,
  trackImageClick,
  trackDetectSignAnalytics
} from "analytics/events";
import { docAPIResponseHandler } from "utils/docAPIResponseHandler";
import { AJSMetadataStatus, AnnotType, STATUS, loadStatus } from "constants/enums";
import { noop } from "utils/lodashUtils";
import { SignalDispatcher } from "dc-preview-utils/src/Utils/SignalDispatcher";
import { apiFactory } from "api/external/ApiFactory";
import { apiNames } from "constants/apiNames";
import {
  GET_COMMENTS_LIST_PRIORITY,
  GET_ACROFORM_LIST_PRIORITY,
  GET_ACROFORM_APPEARANCE_PRIORITY,
  GET_DATA_FROM_NAMES_TREE_PRIORITY,
  ORGANIZE_APIS
} from "constants/constants";
import commandManager from "undoRedo/CommandManager";
import {
  isSinglePageMode,
  getTotalPages,
  getProgressiveRenderInterval,
  getProgressiveRenderMaxInterval,
  getRenderIntervalMultiplier,
  isFullScreenMode,
  getProgressivelyRenderedPageStatus,
  getAssetURI
} from "selectors/documentSelectors";
import { getIsInPlaceMode } from "selectors/viewModeSelectors";
import { getDeviceZoomLevel, getPageZoomLevel } from "selectors/zoom";
import * as stores from "stores";

import { transformViewToPDF } from "dc-preview-utils/src/Utils/PageUtils";
import { getQueryParameterFromUrl } from "dc-preview-utils/src/Utils/UrlUtils";
import { isCommentingAllowed } from "utils/DocPermissionsUtils";
import { CaretShortcut, pdfEditOperations } from "dc-preview-utils/src/Constants/enums";
import { getHCColorPallete } from "../selectors/highContrastSelectors";
import {
  getIsJSScriptExecuting,
  getEnableOverprintSupport,
  getGenAIViewerController,
  isDarkPageThemeEnabled,
  getImageClickSend,
  getEnableCNPDF
} from "selectors/appSelectors";
import { executeWillSaveActions, executeDidSaveActions } from "pdfActionManager/docActionsHelper";
import { JS_DOC_MODIFYING_APIS } from "javascript/jsEngine/constants/constants";
import { checkPageActionsRequired } from "utils/PageActionsUtils";
import { getProgressiveCanvasDetails, checkIfProgressiveRenderingPossible } from "utils/PageUtils";
import OrganizeApisManager from "organize/OrganizeApisManager";
import { getEnableImageSelection } from "selectors/overlayDropinSelectors";
import { performUIUpdates } from "cnpdfUIUpdateManager";
import { logCNPDFUpdateObjectCount } from "utils/CnpdfLoggingUtils";

export default class DocAPI {
  init({ isRichBuild = false, switchPreview = false } = {}) {
    this.ajsMetadataStatus = AJSMetadataStatus.UNKNOWN;
    this.missingFontAnalytics = null;
    if (!switchPreview) this._docApiEvents = new SignalDispatcher();
    this.isRichBuild = isRichBuild;

    this._resetPdfEditedFlags();
    this._oldUndoStack = [];
    this._registerPublicApis();
  }

  _resetPdfEditedFlags() {
    this._isPdfEditedFlag = false;
    this._isPdfEditedInFAS = false;
    this._isPdfEditedInOrganize = false;
    this._isPdfEditedInNumberPages = false;
    this._isPdfEditedInSign = false;
    this._isPdfEditedInEditModeFlag = false;
    this._isPdfEditedInJS = false;
    this._isPdfDirtyByJS = STATUS.UNDEFINED;
  }

  _registerPublicApis() {
    apiFactory.registerApi(apiNames.DOCUMENT, () => ({
      isDirty: this._isDirty.bind(this),
      getUpdatedPDFBuffer: this._getUpdatedPDFBuffer.bind(this),
      getCNPDFUpdates: this._getCNPDFUpdates.bind(this),
      applyCNPDFUpdates: this._applyCNPDFUpdates.bind(this),
      subscribeToPDFEditOperations: this._subscribeToPDFEditOperations.bind(this)
    }));
  }

  _pdfEditedInEditMode(args) {
    const { isDirty, data } = args;
    this._isPdfEditedInEditModeFlag = isDirty;
    this._isPdfDirtyByJS = STATUS.UNDEFINED;
    this._pdfEdited(data.opName, data.opInfo);
  }

  _pdfEdited(opName, opInfo = {}) {
    this._isPdfEditedFlag = true;
    if (
      opName === pdfEditOperations.FAS_FIELD_CREATED ||
      opName === pdfEditOperations.FAS_FIELD_DELETED ||
      opName === pdfEditOperations.FAS_FIELD_EDITED ||
      opName === pdfEditOperations.FAS_FIELDS_FLATTENED
    ) {
      this._isPdfEditedInFAS = true;
    }
    if (
      opName === pdfEditOperations.ORGANIZE_PAGE_ROTATED ||
      opName === pdfEditOperations.ORGANIZE_PAGE_DELETED ||
      opName === pdfEditOperations.ORGANIZE_PAGE_INSERTED ||
      opName === pdfEditOperations.ORGANIZE_PAGE_REORDERED
    ) {
      this._isPdfEditedInOrganize = true;
    }
    if (opName === pdfEditOperations.SIGN_METADATA_EDITED) {
      this._isPdfEditedInSign = true;
    }
    if (opName === pdfEditOperations.NUMBER_PAGES_REMOVED) {
      this._isPdfEditedInNumberPages = true;
    }

    if (opInfo.isJSAction) {
      this._isPdfEditedInJS = true;
    }

    // TODO: Commenting due to the issue of Comments not working for adobetest accounts : https://jira.corp.adobe.com/browse/DCCAM-720
    // if (opInfo.data && opInfo.data.modifiedDate) {
    //   stores.documentStore.setDocInfoProp("ModDate", opInfo.data.modifiedDate);
    // }

    this._docApiEvents.trigger("pdfEdit", opName || "", opInfo);
  }

  _isUndoStackExactlySame() {
    const currUndoStack = commandManager.getUndoStack();
    return this._oldUndoStack.every((elem, index) => elem === currUndoStack[index]);
  }

  _isUndoStackCleared() {
    if (commandManager.getUndoStack().length === 0 && this._oldUndoStack.length === 0) {
      return commandManager.getUndoStackCleared();
    }
    return false;
  }

  _isUndoStackChanged() {
    return (
      commandManager.getUndoStack().length !== this._oldUndoStack.length ||
      !this._isUndoStackExactlySame() ||
      this._isUndoStackCleared()
    );
  }

  _isDirty() {
    return Promise.resolve(this.getIsDirty());
  }

  async _preSaveOperations() {
    await executeWillSaveActions(stores);

    const isDirty = this.getIsDirty();
    this._resetPdfEditedFlags(); // not waiting on buffer api to be successful to mark as non-dirty.

    // TODO: This is temporary, right way is to expose new api from bundle which gives latest file status
    // and genAIViewerController will use that api to reset dirty flag
    // Jira: https://jira.corp.adobe.com/browse/ADCS-45850
    const genAIViewerController = getGenAIViewerController(stores);
    genAIViewerController && genAIViewerController.resetPDFDirtyFlag();
    this._oldUndoStack = commandManager.cloneUndoStack();

    return { isDirty };
  }

  async _postSaveOperations({ isDirty }) {
    isDirty && stores.digSigStore.triggerDigSigValidation();
    await executeDidSaveActions(stores);
  }

  async _getUpdatedPDFBuffer(incremental = false) {
    if (incremental) {
      console.log("Incremental support not implemented yet, falling back to non-incremental approach");
    }
    const { isDirty } = await this._preSaveOperations();

    return this.getContentBuffer().then(async resp => {
      stores.documentStore.setFileSize(resp.data.buffer.length);
      await this._postSaveOperations({ isDirty });
      return resp.data.buffer;
    });
  }

  async _getCNPDFUpdates() {
    const { isDirty } = await this._preSaveOperations();
    let messageObj = {
      command: "getCNPDFUpdates",
      params: {}
    };
    return this._dispatch(messageObj).then(async resp => {
      await this._postSaveOperations({ isDirty });
      return resp;
    });
  }

  async _applyCNPDFUpdates({ manifest, addedComponents = [], updatedComponents = [] }) {
    let messageObj = {
      command: "applyCNPDFUpdates",
      params: {
        manifest,
        addedComponents,
        updatedComponents
      }
    };

    return this._dispatch(messageObj).then(async resp => {
      const response = await performUIUpdates(resp);
      return response;
    });
  }

  _dispatchMessage = messageObj =>
    new Promise((resolve, reject) => {
      this._dispatch(messageObj).then(({ data }) => {
        if (data.status === "failed") {
          reject(data.error);
        } else {
          resolve(data);
        }
      });
    });

  _subscribeToPDFEditOperations(cb) {
    this._docApiEvents.on("pdfEdit", cb);
  }

  getIsDirty() {
    if (this._isPdfDirtyByJS !== STATUS.UNDEFINED) {
      return this._isPdfDirtyByJS;
    }

    return (
      this._isPdfEditedInSign ||
      this._isPdfEditedInFAS ||
      this._isPdfEditedInOrganize ||
      this._isPdfEditedInJS ||
      this._isPdfEditedInNumberPages ||
      (this._isPdfEditedFlag && this._isUndoStackChanged())
    );
  }

  setIsDirty(value) {
    // Notify client regarding explicitly updated isDirty status by JS.
    if (this.getIsDirty() !== value) {
      this._isPdfDirtyByJS = value;
      this._docApiEvents.trigger("pdfEdit", pdfEditOperations.DO_NOT_SAVE, {});
    }
  }

  getFiletype() {
    return this.filetype;
  }

  setFiletype(type) {
    this.filetype = type;
  }

  setAJSMetaDataStatus(value) {
    this.ajsMetadataStatus = value;
  }

  getAJSMetaDataSucceeded() {
    return this.ajsMetadataStatus === AJSMetadataStatus.SUCCEEDED;
  }

  getAJSMetaDataStatus() {
    return this.ajsMetadataStatus;
  }

  reset() {}

  // This function shouldn't get called in base class, always should call specific embedded/dropin class overridden method.
  _dispatch(messageObj) {
    console.log("docAPI interface, wrong workflow, should call specific embedded/dropin dispatch.");
  }

  // Just to tell that dervied class should override this method.
  getMetaData(pdfBuffer, params, password) {
    console.log("control should not come here, should call speific derived class.");
  }

  // Method to get file buffer, this could be PDF or any other document.
  getFileBuffer() {
    console.log("control should not come here, should call speific derived class.");
  }

  // Just to tell that dervied class should override this method.
  isCompletePDFBufferAvailable() {
    console.log("control should not come here, should call specific derived class.");
  }

  // Just to tell that dervied class should override this method.
  isCompleteCNPDFAvailable() {
    console.log("control should not come here, should call specific derived class.");
  }

  getPageRangeGeometry(startPageNum, endPageNum, priority, forceCacheless = false) {
    let messageObj = {
      command: "getPageRangeGeometry",
      params: {
        startPageNum,
        endPageNum,
        priority,
        forceCacheless
      }
    };
    return this._dispatch(messageObj);
  }

  setProgressivePageStatus(index) {
    const progressivelyRenderedPageStatus = getProgressivelyRenderedPageStatus(stores, index);
    const { LOADED, UNLOADED } = loadStatus;
    const isPageProgressivelyRenderedOnce =
      progressivelyRenderedPageStatus &&
      progressivelyRenderedPageStatus !== LOADED &&
      progressivelyRenderedPageStatus !== UNLOADED;

    // Should not override status of a progressive page if the page is already loaded or unloaded
    if (isPageProgressivelyRenderedOnce) {
      // Store pages which are progressively rendered, to avoid multiple renders
      stores.documentStore.setProgressivelyRenderedPageStatus(index, loadStatus.LOADED);
    }
  }

  getRenditionParams(
    itemType,
    index,
    zoomLevel,
    firstVisible,
    lastVisible,
    devicePixelRatio,
    priority,
    oldextraParams = {},
    command,
    colorPallete = null,
    progressiveRenderingWorkflow = null
  ) {
    // Clone extraParams as we are making changes to it.
    const extraParams = JSON.parse(JSON.stringify(oldextraParams));
    if (extraParams.hasOwnProperty("itemsToCache")) {
      extraParams.itemsToCache.forEach((val, key) => {
        extraParams.itemsToCache[key] = val + 1;
      });
    }
    const size = extraParams.size ? extraParams.size : new Number(constants.DEFAULT_PAGE_SIZE).toString();
    const format = extraParams.format ? extraParams.format : "jpg";

    const requestType = extraParams.type && itemType !== "thumbnail" ? extraParams.type : itemType;
    const viewState = {
      pagesInView: {
        start: firstVisible + 1,
        end: lastVisible + 1
      },
      zoomLevel,
      cacheWindow: extraParams.itemsToCache || [],
      allowDiffZoomlevelPages: !!isFullScreenMode(stores)
    };
    delete extraParams.itemsToCache;

    let messageObj = {
      command,
      params: {
        pageNum: index + 1,
        zoomLevel,
        viewState,
        devicePixelRatio,
        showAnnots: extraParams.showAnnots,
        hiddenOCGs: extraParams.hiddenOCGs,
        requestType,
        priority,
        isPreset: isSinglePageMode(stores) || getIsInPlaceMode(stores) || itemType === "thumbnail",
        size,
        format,
        includeActions: checkPageActionsRequired(stores, index),
        enableOverprintSupport: getEnableOverprintSupport(stores),
        ...(colorPallete && { colorPallete })
      }
    };

    // Check if PR possible, then add additional params in getPageRendition call
    // Do not create dynamic canvas here, coz it was already created by documentStore check
    if (
      checkIfProgressiveRenderingPossible({
        stores,
        index,
        itemType,
        priority,
        progressiveRenderingWorkflow
      })
    ) {
      const { canvas, canvasId } = getProgressiveCanvasDetails(index, progressiveRenderingWorkflow);
      messageObj.params.canvas = canvas;
      messageObj.params.canvasId = canvasId;
      stores.documentStore.setProgressivelyRenderedPageStatus(index, loadStatus.BEING_LOADED);
      extraParams.timeLimit = getProgressiveRenderInterval(stores);
      extraParams.maxRenderInterval = getProgressiveRenderMaxInterval(stores);
      extraParams.renderIntervalMultiplier = getRenderIntervalMultiplier(stores);
    }

    if (extraParams && extraParams.tileRect) {
      const { tileRect } = extraParams;
      Object.keys(tileRect).forEach(key => {
        const oldVal = tileRect[key];
        const newVal = oldVal ? oldVal * devicePixelRatio : 0;
        tileRect[key] = Math.floor(newVal);
      });
    }

    const shouldEnableProgressiveRendering =
      getQueryParameterFromUrl(window.location.href, "enableProgressiveRendering") === "true";
    if (shouldEnableProgressiveRendering) {
      extraParams.maxRenderTime = constants.MAX_PAGE_RENDER_TIME;
      extraParams.timeBetweenProgressiveBlits = constants.TIME_BETWEEN_PROGRESSIVE_BLITS;
    }

    Object.assign(messageObj.params, extraParams);

    return messageObj;
  }

  getRendition(
    itemType,
    index,
    zoomLevel,
    firstVisible,
    lastVisible,
    devicePixelRatio,
    priority,
    oldextraParams = {},
    isPreload = false,
    colorPallete = null,
    progressiveRenderingWorkflow = constants.PROGRESSIVE_RENDERING_WORKFLOWS.PAGE_RENDERING
  ) {
    const messageObj = this.getRenditionParams(
      itemType,
      index,
      zoomLevel,
      firstVisible,
      lastVisible,
      devicePixelRatio,
      priority,
      oldextraParams,
      "getPage",
      colorPallete,
      progressiveRenderingWorkflow
    );

    const response = this._dispatch(messageObj, isPreload);
    docAPIResponseHandler(
      response,
      data => {
        if (data.data) {
          const resp = data.data;
          if (itemType === "page") {
            if (resp.missingFontAnalytics) {
              if (this.missingFontAnalytics === null) {
                trackHasUnembeddedFonts(key, resp.missingFontAnalytics[key]);
                this.missingFontAnalytics = {};
              }
              for (var key in resp.missingFontAnalytics) {
                if (resp.missingFontAnalytics[key] && this.missingFontAnalytics[key] !== true) {
                  trackUnembeddedFonts(key, resp.missingFontAnalytics[key]);
                  this.missingFontAnalytics[key] = true;
                }
              }
            }

            if (resp.jp2kAnalytics && Object.keys(resp.jp2kAnalytics).length) {
              trackJP2KAnalytics(resp.jp2kAnalytics);
            }
          }
        }

        this.setProgressivePageStatus(index);

        return data;
      },
      e => {
        // page load failed here
        // NOTE : this will not be called when we get a worker error
        // while loading page, and hence we will not be able to track
        // page load error in that case.
        if (this.getAJSMetaDataSucceeded && this.getAJSMetaDataSucceeded()) {
          trackPageLoadFailed(e ? e.message : "");
        }
      },
      noop,
      noop
    );
    return response;
  }

  getPageThumbnail(pageNum, thumbnailRect) {
    let messageObj = {
      command: "getPage",
      params: {
        pageNum,
        renderRect: thumbnailRect,
        enableOverprintSupport: getEnableOverprintSupport(stores)
      }
    };
    return this._dispatch(messageObj);
  }

  getContentBuffer(decryptedBuffer = false) {
    let messageObj = {
      command: getEnableCNPDF(stores) ? "getPDFBufferForCNPDF" : "getUpdatedBuffer",
      params: {
        decryptedBuffer
      }
    };

    return this._dispatch(messageObj);
  }

  getHyperlinkAnnots(pageIndex, priority = constants.GET_HYPERLINK_LIST_PRIORITY, includeActions = false) {
    let messageObj = {
      command: "getAnnots",
      params: {
        pageNum: pageIndex + 1,
        annotsFilter: AnnotType.LINK,
        priority,
        includeActions
      }
    };
    return this.isCompletePDFBufferAvailable().then(() => this._dispatch(messageObj));
  }

  extractPageText(
    pageIndex,
    getWordRects = false,
    getFormFieldData = false,
    priority = constants.ACCESSIBILITY_CACHE_PRIORITY,
    getCharRects = false // getting used for test api only
  ) {
    let messageObj = {
      command: "extractPageText",
      params: {
        pageNum: pageIndex + 1,
        getWordRects,
        getAnnots: true,
        getTextAttributes: true,
        getFormFieldData,
        priority,
        getCharRects
      }
    };

    return this.isCompletePDFBufferAvailable()
      .then(() => this._dispatch(messageObj))
      .then(extractPageTextResponse => {
        if (!extractPageTextResponse.data.text.aNodes) {
          extractPageTextResponse.data.text.aNodes = {};
        }
        return extractPageTextResponse;
      });
  }

  getContentType(x, y, zoomLevel, index, includeImages, shouldDetectWeblinks, shouldCreateContentPointMap) {
    let messageObj = {
      command: "hitTest",
      params: {
        x,
        y,
        pageNum: index + 1,
        shouldDetectWeblinks,
        shouldCreateContentPointMap,
        includeImages
      }
    };
    return this._dispatch(messageObj);
  }

  // Removes stale offscreen canvas from rendering worker canvas map
  removeOffScreenCanvas(canvasId) {
    let messageObj = {
      command: "removeOffScreenCanvas",
      params: {
        canvasId
      }
    };
    return this._dispatch(messageObj);
  }

  getAcroFormFields(pageIndex, includeSignData = false, priority = GET_ACROFORM_LIST_PRIORITY) {
    let messageObj = {
      command: "getFormFields",
      params: {
        pageNum: pageIndex + 1,
        priority,
        includeActions: true,
        includeSignData
      }
    };
    return this.isCompletePDFBufferAvailable().then(() => this._dispatch(messageObj));
  }

  /**
   *
   * @param {*} pageIndex - 0 based index for the page
   * @param {*} fieldData - the data for the field creation
   * @returns response/error based on the operation.
   */
  createAcroFormField(pageIndex, fieldData) {
    let messageObj = {
      command: "createFormField",
      params: {
        data: {
          pageNum: pageIndex + 1,
          ...fieldData
        }
      }
    };
    this._isPdfDirtyByJS = STATUS.UNDEFINED;
    return this._dispatch(messageObj).then(response => {
      this._pdfEdited(pdfEditOperations.ACROFORM_FIELD_CREATED, { data: response && response.data });
      return response;
    });
  }

  /**
   *
   * @param fields - array of parameters to be passed to createAcroFormField
   * @returns response/error based on the operation.
   */
  createAcroFormFields(fields) {
    let messageObj = {
      command: "createFormFields",
      params: {
        fields
      }
    };
    this._isPdfDirtyByJS = STATUS.UNDEFINED;
    return this._dispatch(messageObj).then(response => {
      this._pdfEdited(pdfEditOperations.ACROFORM_FIELD_CREATED, { data: response && response.data });
      return response;
    });
  }

  /**
   *
   * @param {*} pageIndex - 0 based index for the page
   * @param {*} id  - the id of the filed to edit
   * @param {*} fieldData - the data for the field to be deleted
   * @returns response/error based on the operation.
   */
  deleteAcroFormField(pageIndex, id, fieldData) {
    let messageObj = {
      command: "deleteFormField",
      params: {
        pageNum: pageIndex + 1,
        id,
        ...fieldData
      }
    };
    this._isPdfDirtyByJS = STATUS.UNDEFINED;
    return this._dispatch(messageObj).then(response => {
      this._pdfEdited(pdfEditOperations.ACROFORM_FIELD_DELETED, { data: response && response.data });
      return response;
    });
  }

  /**
   *
   * @param fields - array of parameters to be passed to deleteAcroFormField
   * @returns response/error based on the operation.
   */
  deleteAcroFormFields(fields) {
    let messageObj = {
      command: "deleteFormFields",
      params: {
        fields
      }
    };
    this._isPdfDirtyByJS = STATUS.UNDEFINED;
    return this._dispatch(messageObj).then(response => {
      this._pdfEdited(pdfEditOperations.ACROFORM_FIELD_DELETED, { data: response && response.data });
      return response;
    });
  }

  lockAcroFormFields() {
    let messageObj = {
      command: "lockFormFields",
      params: {}
    };
    this._isPdfDirtyByJS = STATUS.UNDEFINED;
    return this._dispatch(messageObj).then(response => {
      this._pdfEdited(pdfEditOperations.ACROFORM_FIELDS_LOCKED, { data: response && response.data });
      return response;
    });
  }

  getDocumentParts({ includeSignData = false }) {
    let messageObj = {
      command: "getDocumentParts",
      params: {
        includeSignData
      }
    };
    return this.isCompletePDFBufferAvailable().then(() => this._dispatch(messageObj));
  }

  setDocumentParts(data) {
    let messageObj = {
      command: "setDocumentParts",
      params: {
        data
      }
    };
    this._isPdfDirtyByJS = STATUS.UNDEFINED;
    return this._dispatch(messageObj).then(data => {
      this._pdfEdited(pdfEditOperations.SIGN_METADATA_EDITED);
      return data;
    });
  }

  getSignMetadata() {
    let messageObj = {
      command: "getSignMetadata",
      params: {
        data: {}
      }
    };
    return this.isCompletePDFBufferAvailable().then(() => this._dispatch(messageObj));
  }

  updateSignMetadata(signData) {
    let messageObj = {
      command: "updateSignMetadata",
      params: {
        data: {
          ...signData
        }
      }
    };
    this._isPdfDirtyByJS = STATUS.UNDEFINED;
    return this._dispatch(messageObj).then(response => {
      this._pdfEdited(pdfEditOperations.SIGN_METADATA_EDITED, { data: response && response.data });
      return response;
    });
  }

  jsSpecificAPICall(params) {
    params.addResultToSAB = true;
    let messageObj = {
      command: "JSApi",
      params: {
        params
      }
    };
    if (JS_DOC_MODIFYING_APIS.includes(params.apiName)) {
      this._isPdfDirtyByJS = STATUS.UNDEFINED;
    }
    return this._dispatch(messageObj).then(data => {
      if (JS_DOC_MODIFYING_APIS.includes(params.apiName)) {
        this._pdfEdited(pdfEditOperations.PDF_MODIFIED, { isJSAction: true });
        return data;
      }
    });
  }

  updateDocInfo(data) {
    const isJSAction = getIsJSScriptExecuting(stores);
    let messageObj = {
      command: "updateDocInfo",
      params: {
        data,
        isJSAction
      }
    };

    this._isPdfDirtyByJS = STATUS.UNDEFINED;
    return new Promise((resolve, reject) => {
      this._dispatch(messageObj).then(({ data }) => {
        if (data.status === "failed") {
          reject(data.error);
        } else {
          this._pdfEdited(pdfEditOperations.PDF_MODIFIED, { isJSAction, data });
          resolve(data);
        }
      });
    });
  }

  addIcon(newIconName, iconName) {
    const isJSAction = getIsJSScriptExecuting(stores);
    let messageObj = {
      command: "addIcon",
      params: {
        iconName,
        newIconName,
        isJSAction
      }
    };

    this._isPdfDirtyByJS = STATUS.UNDEFINED;
    return new Promise((resolve, reject) => {
      this._dispatch(messageObj).then(({ data }) => {
        if (data.status === "failed") {
          reject(data.error);
        } else {
          this._pdfEdited(pdfEditOperations.PDF_MODIFIED, { isJSAction, data });
          resolve(data);
        }
      });
    });
  }

  /**
   *
   * @param {*} pageIndex - 0 based index for the page
   * @param {*} id  - the id of the filed to edit
   * @param {*} data - the data for the field update
   * @returns response/error based on the operation.
   */
  editAcroFormField(pageIndex, id, data) {
    const isJSAction = getIsJSScriptExecuting(stores);
    let messageObj = {
      command: "updateFormField",
      params: {
        id,
        pageNum: pageIndex + 1,
        data,
        authoringMode: data.authoringMode,
        isJSAction
      }
    };
    this._isPdfDirtyByJS = STATUS.UNDEFINED;
    return this._dispatch(messageObj).then(response => {
      this._pdfEdited(pdfEditOperations.ACROFORM_FIELD_EDITED, { isJSAction, data: response && response.data });
      return response;
    });
  }

  editAcroFormFields(fields) {
    const isJSAction = getIsJSScriptExecuting(stores);
    let messageObj = {
      command: "updateFormFields",
      params: {
        fields
      }
    };
    this._isPdfDirtyByJS = STATUS.UNDEFINED;
    return this._dispatch(messageObj).then(response => {
      this._pdfEdited(pdfEditOperations.ACROFORM_FIELD_EDITED, { isJSAction, data: response && response.data });
      return response;
    });
  }

  getDataFromNamesTree(params, priority = GET_DATA_FROM_NAMES_TREE_PRIORITY) {
    const messageObj = {
      command: "getDataFromNamesTree",
      params: { dataToFetch: params.dataToFetch, priority }
    };
    return this._dispatchMessage(messageObj);
  }

  getCommentsList(pageIndex, priority = GET_COMMENTS_LIST_PRIORITY) {
    const annotsThatAreNotMarkup = [
      "Link",
      "Popup",
      "Movie",
      "RichMedia",
      "Widget",
      "Screen",
      "PrinterMark",
      "TrapNet",
      "AcroForm",
      "Watermark",
      "3D"
    ];

    let messageObj = {
      command: "getAnnots",
      params: {
        pageNum: pageIndex + 1,
        annotsFilter: AnnotType.ALL,
        priority
      }
    };
    return this.isCompletePDFBufferAvailable().then(() =>
      this._dispatch(messageObj).then(data => {
        let commentsList = data.data.annots;
        if (commentsList) {
          let comments = commentsList || [];
          comments = comments.filter(comment => !annotsThatAreNotMarkup.includes(comment.type));
          data.params = {
            commentsList: {
              comments
            }
          };

          /* to do : uncomment this when changes in comment dropin will be done
          data.data.commentsList = {
            comments
          };
          */
          stores.jsStore.resetAnnotsData();
        }
        return data;
      })
    );
  }

  async getCommentsCount() {
    let totalCommentsCount = 0;
    let annotsData;
    let commentsList;
    const totalPages = getTotalPages(stores);
    /* eslint-disable no-await-in-loop */
    for (let pageNumber = 0; pageNumber < totalPages; pageNumber++) {
      annotsData = await this.getCommentsList(pageNumber);
      commentsList = annotsData.params.commentsList.comments;
      if (commentsList) {
        totalCommentsCount += commentsList.length;
      }
    }
    /* eslint-enable no-await-in-loop */

    return totalCommentsCount;
  }

  getCommentAppearance(pageIndex, id, data, priority = GET_ACROFORM_APPEARANCE_PRIORITY) {
    const colorPallete =
      isDarkPageThemeEnabled(stores) && data.useDarkThemeColorPallete
        ? stores.pageThemeSwitcherStore.getcolorPallete()
        : getHCColorPallete(stores);
    let messageObj = {
      command: "getAnnotAppearance",
      params: {
        pageNum: pageIndex + 1,
        id,
        appearanceSubDict: data.appearanceType,
        appearanceState: data.appearanceState,
        zoomLevel: getPageZoomLevel(stores, pageIndex),
        devicePixelRatio: window.devicePixelRatio,
        priority,
        ...(colorPallete && { colorPallete }),
        ignoreDisplayFlags: data.ignoreDisplayFlags || false, // if true, gets appearance for hidden annots also
        enableOverprintSupport: getEnableOverprintSupport(stores)
      }
    };

    return this._dispatch(messageObj);
  }

  getVoiceNoteData(pageNum, annotId) {
    const messageObj = {
      command: "getVoiceAnnotData",
      params: {
        pageNum,
        annotId
      }
    };
    return this._dispatch(messageObj);
  }

  createComment(pageIndex, commentData) {
    if (isCommentingAllowed()) {
      const pageNum = pageIndex + 1;
      let messageObj = {
        command: "createAnnot",
        params: {
          data: {
            pageNum,
            ...commentData
          }
        }
      };
      this._isPdfDirtyByJS = STATUS.UNDEFINED;
      return this._dispatch(messageObj).then(response => {
        this._pdfEdited(pdfEditOperations.COMMENT_CREATED, { pageNum, data: response && response.data });
        const id = response.data.id;
        response.params = {
          commentData: {
            id: response.data.id,
            comment: response.data.annot
          }
        };
        return response;
      });
    }
    return Promise.reject({ status: "Comment can't be created on this file" });
  }

  createComments(commentsData) {
    if (isCommentingAllowed()) {
      if (commentsData && commentsData.length === 0) return Promise.resolve();
      let messageObj = {
        command: "createAnnots",
        params: {
          data: {
            commentsData
          }
        }
      };
      this._isPdfDirtyByJS = STATUS.UNDEFINED;
      return this._dispatch(messageObj).then(response => {
        this._pdfEdited(pdfEditOperations.COMMENTS_CREATED, { data: response && response.data });
        return response;
      });
    }
    return Promise.reject({ status: "Comment can't be created on this file" });
  }

  editComment(pageIndex, id, data, isCommentEditedByJS = false) {
    const isJSAction = getIsJSScriptExecuting(stores) || isCommentEditedByJS;
    if (isCommentingAllowed()) {
      const pageNum = pageIndex + 1;
      let messageObj = {
        command: "editAnnot",
        params: {
          id,
          pageNum,
          data
        }
      };
      this._isPdfDirtyByJS = STATUS.UNDEFINED;
      return this._dispatch(messageObj).then(response => {
        this._pdfEdited(pdfEditOperations.COMMENT_EDITED, { pageNum, data: response && response.data, isJSAction });
        response.params = {
          status: {
            status: response.data.status,
            comment: response.data.annot
          }
        };
        return response;
      });
    }
    return Promise.reject({ status: "Comment can't be edited on this file" });
  }

  deleteComment(pageIndex, id) {
    if (isCommentingAllowed()) {
      const pageNum = pageIndex + 1;
      let messageObj = {
        command: "deleteAnnot",
        params: {
          id,
          pageNum
        }
      };
      this._isPdfDirtyByJS = STATUS.UNDEFINED;
      return this._dispatch(messageObj).then(response => {
        this._pdfEdited(pdfEditOperations.COMMENT_DELETED, { pageNum, data: response && response.data });
        response.params = {
          status: response.data.status
        };
        return response;
      });
    }
    return Promise.reject({ status: "Comment can't be deleted on this file" });
  }

  deleteComments(commentsData) {
    if (isCommentingAllowed()) {
      if (commentsData && commentsData.length === 0) return Promise.resolve();
      let messageObj = {
        command: "deleteAnnots",
        params: {
          data: {
            commentsData
          }
        }
      };
      this._isPdfDirtyByJS = STATUS.UNDEFINED;
      return this._dispatch(messageObj).then(response => {
        this._pdfEdited(pdfEditOperations.COMMENTS_DELETED, { data: response && response.data });
        response.params = {
          status: response.data.status
        };
        return response;
      });
    }
    return Promise.reject({ status: "Comment can't be deleted on this file" });
  }

  getFASFields(pageNum) {
    let messageObj = {
      command: "getFASFields",
      params: {
        pageNum
      }
    };
    this._isPdfDirtyByJS = STATUS.UNDEFINED;
    return this._dispatchMessage(messageObj).then(data => {
      if (data && data.fieldsFlattened) this._pdfEdited(pdfEditOperations.FAS_FIELDS_FLATTENED);
      return data;
    });
  }

  getOverprintType(pageIndex, priority = constants.LOW_PRIORITY) {
    let messageObj = {
      command: "getOverprintType",
      params: {
        pageNum: pageIndex + 1,
        priority
      }
    };
    return this._dispatchMessage(messageObj);
  }

  createFASField(pageNum, data) {
    let messageObj = {
      command: "createFASField",
      params: {
        pageNum,
        data
      }
    };
    this._isPdfDirtyByJS = STATUS.UNDEFINED;
    return new Promise((resolve, reject) => {
      this._dispatch(messageObj).then(({ data }) => {
        if (data.status === "failed") {
          reject(data.error);
        } else {
          this._pdfEdited(pdfEditOperations.FAS_FIELD_CREATED, { pageNum, data });
          resolve(data);
        }
      });
    });
  }

  deleteFASField(pageNum, id) {
    let messageObj = {
      command: "deleteFASField",
      params: {
        pageNum,
        id
      }
    };
    this._isPdfDirtyByJS = STATUS.UNDEFINED;
    return new Promise((resolve, reject) => {
      this._dispatch(messageObj).then(({ data }) => {
        if (data.status === "failed") {
          reject(data.error);
        } else {
          this._pdfEdited(pdfEditOperations.FAS_FIELD_DELETED, { pageNum, data });
          resolve(data);
        }
      });
    });
  }

  updateFASField(pageNum, id, data) {
    let messageObj = {
      command: "updateFASField",
      params: {
        pageNum,
        id,
        data
      }
    };
    this._isPdfDirtyByJS = STATUS.UNDEFINED;
    return new Promise((resolve, reject) => {
      this._dispatch(messageObj).then(({ data }) => {
        if (data.status === "failed") {
          reject(data.error);
        } else {
          this._pdfEdited(pdfEditOperations.FAS_FIELD_EDITED, { pageNum, data });
          resolve(data);
        }
      });
    });
  }

  flattenFASFields(forceFlatten) {
    let messageObj = {
      command: "flattenFASFields",
      params: { forceFlatten }
    };
    this._isPdfDirtyByJS = STATUS.UNDEFINED;
    return new Promise((resolve, reject) => {
      this._dispatch(messageObj).then(({ data }) => {
        if (data.status === "failed") {
          reject(data.error);
        } else {
          this._pdfEdited(pdfEditOperations.FAS_FIELDS_FLATTENED, { data });
          resolve(data);
        }
      });
    });
  }

  getFASFieldAppearance(pageNum, id) {
    let messageObj = {
      command: "getFASFieldAppearance",
      params: {
        pageNum,
        id,
        zoomLevel: getDeviceZoomLevel(stores, pageNum - 1),
        enableOverprintSupport: getEnableOverprintSupport(stores)
      }
    };
    return this._dispatchMessage(messageObj);
  }

  selectWord(startPointInView, priority) {
    const startPoint = transformViewToPDF(startPointInView.x, startPointInView.y, startPointInView.pageNum - 1, stores);
    startPoint.pageNum = startPointInView.pageNum;
    let messageObj = {
      command: "selectContent",
      params: {
        startPoint,
        snapToWords: true,
        priority
      }
    };
    return this._dispatch(messageObj);
  }

  textSelectionFromPDFCoordinates(startPoint, endPoint, priority, snapToWords, getCaret, force) {
    let messageObj = {
      command: "selectContent",
      params: {
        startPoint,
        endPoint,
        snapToWords: Boolean(snapToWords),
        priority,
        getCaret,
        force: Boolean(force)
      }
    };
    return this._dispatch(messageObj);
  }

  imageSelection(startPointInView, endPointInView, priority) {
    const startPoint = transformViewToPDF(startPointInView.x, startPointInView.y, startPointInView.pageNum - 1, stores);
    startPoint.pageNum = startPointInView.pageNum;
    const endPoint = transformViewToPDF(endPointInView.x, endPointInView.y, endPointInView.pageNum - 1, stores);
    endPoint.pageNum = endPointInView.pageNum;
    let messageObj = {
      command: "selectContent",
      params: {
        startPoint,
        endPoint,
        priority
      }
    };
    const enableImageSelection = getEnableImageSelection(stores);
    if (!getImageClickSend(stores)) {
      trackImageClick();
    }
    if (enableImageSelection) {
      return this._dispatch(messageObj);
    }
    return Promise.resolve();
  }

  textSelection(startPointInView, endPointInView, priority, snapToWords, getCaret = false, force = false) {
    const startPoint = transformViewToPDF(startPointInView.x, startPointInView.y, startPointInView.pageNum - 1, stores);
    startPoint.pageNum = startPointInView.pageNum;
    const endPoint = transformViewToPDF(endPointInView.x, endPointInView.y, endPointInView.pageNum - 1, stores);
    endPoint.pageNum = endPointInView.pageNum;
    return this.textSelectionFromPDFCoordinates(
      startPoint,
      endPoint,
      priority,
      snapToWords,
      getCaret,
      force || getCaret
    );
  }

  keyboardSelection(endPointInView, direction, snapToWords, priority, jumpTo = CaretShortcut.NONE, getCaret = false) {
    let endPoint;
    if (endPointInView) {
      endPoint = transformViewToPDF(endPointInView.x, endPointInView.y, endPointInView.pageNum - 1, stores);
      endPoint.pageNum = endPointInView.pageNum;
    }
    let messageObj = {
      command: "selectContent",
      params: {
        endPoint,
        direction,
        snapToWords: Boolean(snapToWords),
        priority,
        jumpTo,
        getCaret
      }
    };
    return this._dispatch(messageObj);
  }

  copyTextInSelection(richText = false) {
    let messageObj = {
      command: "getSelectedText",
      params: { richText }
    };
    return this._dispatch(messageObj);
  }

  isHyperlinkClicked(x, y, pageIndex, zoomLevel, priority) {
    const pdfPoint = transformViewToPDF(x, y, pageIndex, stores);

    let messageObj = {
      command: "getHyperlinkData",
      params: {
        x: pdfPoint.x,
        y: pdfPoint.y,
        pageNum: pageIndex + 1,
        priority
      }
    };
    return this._dispatch(messageObj);
  }

  performSearch(text, startPage, numPages, priority, zoomLevel = 1) {
    let messageObj = {
      command: "findText",
      params: {
        text,
        startPage,
        numPages,
        zoomLevel,
        priority
      }
    };
    return this._dispatch(messageObj);
  }

  async switchToCNPDF(cnpdfFetcher) {
    const manifest = await cnpdfFetcher.getManifest();
    logCNPDFUpdateObjectCount(manifest);

    let messageObj = {
      command: "switchToCNPDF",
      params: {
        manifest,
        cnpdfFetcher
      }
    };

    return this._dispatch(messageObj);
  }

  loadAttachmentsList() {
    let messageObj = {
      command: "getAttachments",
      params: {}
    };
    return this._dispatch(messageObj);
  }

  fetchAttachmentFile(fileName) {
    let messageObj = {
      command: "getAttachmentBuffer",
      params: {
        name: fileName
      }
    };
    return this._dispatch(messageObj);
  }

  loadBookmarks() {
    let messageObj = {
      command: "getBookmarks",
      params: {}
    };
    return this._dispatch(messageObj);
  }

  forceSwitchJS() {
    let messageObj = {
      command: "forceSwitchJS",
      params: {}
    };
    return this.isCompletePDFBufferAvailable().then(() => this._dispatch(messageObj));
  }

  clearMessageQueue(params) {
    let messageObj = {
      command: "clearMessageQueue",
      params
    };
    return this._dispatch(messageObj);
  }

  getPageFontList(params) {
    let messageObj = {
      command: "getPageFontList",
      params
    };
    return this._dispatch(messageObj).then(resp => {
      let fontList = resp.data.fontList;
      stores.documentStore.setFontList(fontList, params.pageNum - 1);
      return resp.data;
    });
  }

  isSwitchedJS() {
    return false;
  }

  isExternalAsset() {
    return false;
  }

  isFileQualifiedForLiquidMode() {
    let messageObj = {
      command: "isFileQualifiedForLiquidMode",
      params: {}
    };
    return this._dispatch(messageObj);
  }

  translateToLiquidModeView() {
    let messageObj = {
      command: "translateToLiquidModeView",
      params: {}
    };
    return this._dispatch(messageObj);
  }

  getLiquidModeViewResource(resourceID, resourceURL) {
    let messageObj = {
      command: "getLiquidModeViewResource",
      params: {
        resourceID,
        resourceURL
      }
    };
    return this._dispatch(messageObj);
  }

  getHtmlLocationForDocPoint(page, x, y) {
    let messageObj = {
      command: "getHtmlLocationForDocPoint",
      params: {
        page,
        x,
        y
      }
    };
    return this._dispatch(messageObj);
  }

  getDocPointForHtmlLocation(elementID, offset) {
    let messageObj = {
      command: "getDocPointForHtmlLocation",
      params: {
        elementID,
        offset
      }
    };
    return this._dispatch(messageObj);
  }

  // The token will be send when this api is called from the prehandler of undo-redo
  // In undo-redo we need to send this token in rest cases the token will be empty
  cropWillBegin(fileUrl, token) {
    const messageObj = {
      command: "cropWillBegin",
      params: {
        fileUrl,
        token
      }
    };
    return this._dispatch(messageObj);
  }

  cropPages(startIndex, endIndex, cropMargins) {
    const messageObj = {
      command: "cropPages",
      params: { startIndex, endIndex, cropMargins }
    };
    return this._dispatch(messageObj).then(data => data);
  }

  // The token will be send when this api is called from the prehandler of undo-redo
  // In undo-redo we need to send this token in rest cases the token will be empty
  numberPagesWillBegin(fileUrl, token) {
    const messageObj = {
      command: "numberPagesWillBegin",
      params: {
        fileUrl,
        token
      }
    };
    return this._dispatch(messageObj);
  }

  addNumberPage(startIndex, endIndex, startPageNumber, fontStyle, position) {
    const messageObj = {
      command: "addNumberPage",
      params: { startIndex, endIndex, startPageNumber, fontStyle, position }
    };
    return this._dispatch(messageObj).then(data => data);
  }

  getPagesScanStatus(startPageIndex, endPageIndex, ocrType, priority = constants.GET_PAGES_SCAN_STATUS_PRIORITY) {
    const messageObj = {
      command: "getPagesScanStatus",
      params: { startPageIndex, endPageIndex, ocrType, priority }
    };
    return this._dispatch(messageObj).then(data => data);
  }

  getIsScannedDocument(priority = constants.GET_IS_SCANNED_DOCUMENT) {
    const messageObj = {
      command: "isScannedDocument",
      params: { priority }
    };
    return this._dispatch(messageObj).then(data => data);
  }

  getIsScannedDocumentFromAJSProvider = async () => {
    let fileHandle = null;
    let ajs = null;
    try {
      const ajsPromise = providers.acrobatjs();
      const fileBuffer = await this.getFileBuffer();
      const openPdfParams = {
        buffer: fileBuffer
      };
      ajs = await ajsPromise;
      fileHandle = await ajs.openPDF(openPdfParams);
      const isScannedDocument = await ajs.isScannedDocument(fileHandle);
      if (isScannedDocument && isScannedDocument.data) {
        return Promise.resolve(isScannedDocument.data.isScannedDocument);
      }
      return Promise.resolve(false);
    } catch (e) {
      console.error("Error: ", e);
      return Promise.reject(e);
    } finally {
      if (fileHandle) {
        ajs.closePDF(fileHandle);
      }
    }
  };

  cropDidEnd(token, domain, docHashSelector, useDocHashForThrottling) {
    const messageObj = {
      command: "cropDidEnd",
      params: {
        token,
        domain,
        docHashSelector,
        useDocHashForThrottling
      }
    };
    return this._dispatch(messageObj).then(data => data);
  }

  docHasHFWB() {
    const messageObj = {
      command: "docHasHFWB",
      params: {}
    };
    return this._dispatch(messageObj).then(data => data);
  }

  removePageNumbers(startIndex, endIndex) {
    const messageObj = {
      command: "removePageNumbers",
      params: { startIndex, endIndex }
    };
    return this._dispatch(messageObj).then(data => data);
  }

  numberPagesDidEnd(token, domain, docHashSelector, useDocHashForThrottling) {
    const messageObj = {
      command: "numberPagesDidEnd",
      params: {
        token,
        domain,
        docHashSelector,
        useDocHashForThrottling
      }
    };
    return this._dispatch(messageObj).then(data => data);
  }

  /**
   *
   * @param {*} pageIndex - 0 based pageIndex - The page index to get link annots for
   * @param {*} includeSignData - boolean to indicate if the sign data needs to be included
   * @param {*} priority - The priority of the command to be executed
   * @returns - list of hyper links for the provided pageIndex
   */
  getLinkAnnots(pageIndex, includeSignData = false, priority = constants.GET_HYPERLINK_LIST_PRIORITY) {
    let messageObj = {
      command: "getLinkAnnots",
      params: {
        pageNum: pageIndex + 1,
        includeSignData,
        priority
      }
    };
    return this.isCompletePDFBufferAvailable().then(() => this._dispatch(messageObj));
  }

  /**
   *
   * @param {*} pageIndex - 0 based index for the page to create link for
   * @param {*} linkData - the data for the link creation
   * @returns response/error based on the operation.
   */
  createLinkAnnot(pageIndex, linkData) {
    let messageObj = {
      command: "createLinkAnnot",
      params: {
        pageNum: pageIndex + 1,
        data: {
          ...linkData
        }
      }
    };
    return this._dispatch(messageObj).then(response => {
      this._pdfEdited(pdfEditOperations.HYPERLINK_CREATED, { data: response && response.data });
      return response;
    });
  }

  /**
   *
   * @param {*} pageIndex - 0 based index for the page to edit link for
   * @param {*} id - id for the link to be updated
   * @param {*} linkData - the data for the link to be edited
   * @returns response/error based on the operation.
   */
  editLinkAnnot(pageIndex, id, { shouldUpdatePDFBuffer, ...linkData }) {
    let messageObj = {
      command: "editLinkAnnot",
      params: {
        pageNum: pageIndex + 1,
        shouldUpdatePDFBuffer,
        id,
        data: {
          ...linkData
        }
      }
    };
    return this._dispatch(messageObj).then(response => {
      this._pdfEdited(pdfEditOperations.HYPERLINK_EDITED, { data: response && response.data });
      return response;
    });
  }

  /**
   *
   * @param {*} links - the data for the links to be edited
   * @returns response/error based on the operation.
   */
  editLinkAnnots(links) {
    let messageObj = {
      command: "editLinkAnnots",
      params: {
        links
      }
    };
    this._isPdfDirtyByJS = STATUS.UNDEFINED;
    return this._dispatch(messageObj).then(response => {
      this._pdfEdited(pdfEditOperations.HYPERLINK_EDITED, { data: response && response.data });
      return response;
    });
  }

  /**
   *
   * @param {*} pageIndex - 0 based index for the page to create link for
   * @param {*} id - id for the link to be deleted
   * @param linkData - the data for the links to be deleted
   * @returns response/error based on the operation.
   */
  deleteLinkAnnot(pageIndex, id, linkData) {
    let messageObj = {
      command: "deleteLinkAnnot",
      params: {
        pageNum: pageIndex + 1,
        id,
        data: {
          ...linkData
        }
      }
    };
    return this._dispatch(messageObj).then(response => {
      this._pdfEdited(pdfEditOperations.HYPERLINK_DELETED, { data: response && response.data });
      return response;
    });
  }

  /**
   *
   * @param links - array of parameters to be passed to deleteLinkAnnot
   * @returns response/error based on the operation.
   */
  deleteLinkAnnots(links) {
    let messageObj = {
      command: "deleteLinkAnnots",
      params: {
        links
      }
    };
    return this._dispatch(messageObj).then(response => {
      this._pdfEdited(pdfEditOperations.HYPERLINK_DELETED, { data: response && response.data });
      return response;
    });
  }

  verifyDigSig(externalCertificateValidator) {
    const messageObj = {
      command: "verifyDigitalSignatures",
      params: {
        externalCertificateValidator
      }
    };
    return this._dispatch(messageObj);
  }

  /**
   * API to detect langauge from text or document
   * @param params for api
   * @returns languge of document or text(if provided)
   */
  async detectLanguage(params = {}) {
    const { text, useDocAsFallback } = params;
    let messageObj = {
      command: "detectLanguage",
      params: {
        text,
        useDocAsFallback
      }
    };
    const detectLanguageResponse = await this._dispatch(messageObj);
    return (detectLanguageResponse.data && detectLanguageResponse.data.detectedLanguage.toLowerCase()) || "un";
  }

  /**
   * API to get the image buffer for the given page number, image rect
   * @param {object} params - parameters for the api
   * @param {number} params.pageNum - page number for which image buffer is required
   * @param {object} params.croppedBounds - cropped rect object for the image in default user space
   * @param {object} params.unCroppedBounds - un-cropped rect object for the image in default user space
   * @param {number} params.boundCheckTolerance - relax the bound check by this value, default: 2
   * @returns {promise} that resolves with image buffer if found, otherwise resolves with error message
   * */
  getImageBuffer(params = {}) {
    const messageObj = {
      command: "getImageBuffer",
      params
    };
    return this._dispatch(messageObj);
  }

  /**
   * Resolves with C2PA metadata (if present) of the given image buffer
   * Supported file type: PNG
   * @param {Object} params - params
   * @param {ArrayBuffer} params.imageBuffer - image buffer
   * @return {Promise} - a promise which resolves with C2PA metadata of the image.
   */
  getImageC2PAMetadata(params = {}) {
    const messageObj = {
      command: "getImageC2PAMetadata",
      params
    };
    return this._dispatch(messageObj);
  }

  preProcessingHandler(apiName, params) {
    const apisMap = {
      organizeWillBegin: () => OrganizeApisManager.organizeWillBegin(params),
      organizeDidEnd: () => OrganizeApisManager.organizeDidEnd(params)
    };
    if (apisMap[apiName]) {
      return apisMap[apiName](params);
    }
    return params;
  }

  checkIfError(response) {
    if (response && response.data && response.data.status === "failed") {
      throw new Error(response.data.error);
    }
  }

  commonResponseHandler(apiName, response) {
    if (ORGANIZE_APIS.has(apiName)) {
      this.checkIfError(response);
    }
    return response;
  }

  responseHandler(apiName, response, options) {
    const apisMap = {
      organizeWillBegin: () => OrganizeApisManager.organizeWillBeginResponseHandler(response, options),
      organizeDidEnd: () => OrganizeApisManager.organizeDidEndResponseHandler(response, options)
    };
    if (apisMap[apiName]) {
      return apisMap[apiName](response, options);
    }
    return this.commonResponseHandler(apiName, response, options);
  }

  async organizeWillBegin(params = {}) {
    await this.preProcessingHandler("organizeWillBegin", params);
    const messageObj = {
      command: "organizeWillBegin",
      params
    };
    const response = await this._dispatch(messageObj);
    return this.responseHandler("organizeWillBegin", response);
  }

  async rotatePages(params = {}) {
    await this.preProcessingHandler("rotatePages", params);
    const messageObj = {
      command: "rotatePages",
      params
    };
    const response = await this._dispatch(messageObj);
    return this.responseHandler("rotatePages", response);
  }

  async deletePages(params = {}) {
    await this.preProcessingHandler("deletePages", params);
    const messageObj = {
      command: "deletePages",
      params
    };
    const response = await this._dispatch(messageObj);
    return this.responseHandler("deletePages", response);
  }

  async insertPages(params = {}) {
    await this.preProcessingHandler("insertPages", params);
    const messageObj = {
      command: "insertPages",
      params
    };
    const response = await this._dispatch(messageObj);
    return this.responseHandler("insertPages", response);
  }

  async reorderPages(params = {}) {
    await this.preProcessingHandler("reorderPages", params);
    const messageObj = {
      command: "reorderPages",
      params
    };
    const response = await this._dispatch(messageObj);
    return this.responseHandler("reorderPages", response);
  }

  async organizeDidEnd(params = {}) {
    await this.preProcessingHandler("organizeDidEnd", params);
    const messageObj = {
      command: "organizeDidEnd",
      params
    };
    const response = await this._dispatch(messageObj);
    return this.responseHandler("organizeDidEnd", response);
  }

  async _editWillBegin(params = {}) {
    const editProvider = await providers["edit-provider"]();
    await editProvider.loadEditModule();

    const assetURI = getAssetURI(stores);
    await editProvider.setupPluginIfRequired(assetURI);

    const appLocale = locale2.getLocale();
    const browserLocale = navigator.language || navigator.userLanguage;

    const messageObj = {
      command: "editWillBegin",
      params: {
        fileUrl: assetURI,
        pageIndex: params.pageIndex,
        appLocale,
        browserLocale
      }
    };
    return this._dispatch(messageObj);
  }

  async _editDidEnd(params = {}) {
    const messageObj = {
      command: "editDidEnd",
      params
    };
    return this._dispatch(messageObj);
  }

  /**
   * API to get bboxes for the page
   * @param {number} params.pageIndex - 0 based page index
   * @returns {promise} that resolves with bboxes for the page
   */
  async getBBoxForPage(params = {}) {
    await this._editWillBegin({ pageIndex: params.pageIndex });

    const partialBboxResp = await this._dispatch({
      command: "getPartialBBoxForPage",
      params: {
        pageIndex: params.pageIndex,
        shouldCreateBBoxHash: true
      }
    });

    const { bBoxHash } = partialBboxResp.data;
    const editProvider = await providers["edit-provider"]();
    const token = await editProvider.getAuthorizationToken(bBoxHash);
    const bboxResp = await this._dispatch({
      command: "getEditableItemsOnPage",
      params: {
        pageIndex: params.pageIndex,
        authorizationInfo: {
          token,
          domain: window.location.origin
        }
      }
    });

    const { editableItems } = bboxResp.data;
    editableItems.map((item, index) => {
      item.index = index;
      return item;
    });

    await this._editDidEnd();
    return bboxResp;
  }

  /**
   * API to get the content of the bbox
   * @param {object} params - parameters for the api
   * @param {number} params.pageIndex - 0 based page index
   * @param {number} params.itemIndex - The index of the bbox
   * @param {object} params.bboxBounds - The bounds of the bbox in pdf points
   * @returns {promise} that resolves with the content of the bbox
   **/
  async getBBoxContent({ pageIndex, itemIndex, bboxBounds }) {
    await this._editWillBegin({ pageIndex });
    await this._dispatch({
      command: "activateEditableItem",
      params: {
        pageIndex,
        itemIndex,
        keyboardModifierFlags: 0,
        isSelectAllSelected: false,
        annots: []
      }
    });

    const itemContentResp = await this._dispatch({
      command: "getActiveItemContent",
      params: { getListInfo: false }
    });

    await this._editDidEnd();
    return itemContentResp;
  }
}
